import { axiosGet } from "@/services/axiosService";
import { useQueries } from "@tanstack/react-query";

export interface UseResponseCacheReturn {
  questions?: QuestionData[];
  sentiment?: QuestionSentiment;
  pagination?: QuestionPagination;
  isLoading: boolean;
  isError: boolean;
  error?: any;
}

export const useResponseCache = ({
  selectedBot,
  fromDate,
  toDate,
  selectedFilter = "all",
  searchString = "",
  page = 1,
  pageSize = 6,
  selectedCategory,
  hidePagination,
  calledFromBuilder,
  sort,
}: UseResponseCacheParams) => {
  const botCustomId = selectedBot?.botCustomId;

  const queries = useQueries({
    queries: [
      {
        queryKey: [
          "allQuestionsData",
          fromDate,
          toDate,
          botCustomId,
          selectedFilter,
          searchString,
          page,
          pageSize,
          selectedCategory,
          sort,
        ],
        queryFn: () => {
          const params = new URLSearchParams({
            botCustomId: botCustomId || "",
            selectedFilter,
            searchString: encodeURIComponent(searchString),
            page: page.toString(),
            pageSize: pageSize.toString(),
          });

          if (fromDate) params.append("fromDate", fromDate.toString());
          if (toDate) params.append("toDate", toDate.toString());
          if (selectedCategory)
            params.append("category", selectedCategory.toString());
          if (sort) params.append("sort", sort.toString());

          return axiosGet(`/analysis/allQuestionsData?${params.toString()}`);
        },
        select: (response: any) => response.data,
      },
    ],
  });
  const responseData = queries[0].data as any;

  return {
    questions: responseData?.data?.questions,
    sentiment: responseData?.data?.sentiment,
    pagination: responseData?.data?.pagination,
    isLoading: queries.some((query) => query.isLoading),
    isFetching: queries.some((query) => query.isFetching),
    isError: queries.some((query) => query.isError),
    refetch: queries[0].refetch,
    error: queries.find((query) => query.isError)?.error,
  } as UseResponseCacheReturn;
};

export type QuestionFilterType =
  | "all"
  | "answered"
  | "most_asked"
  | "unanswered"
  | "helpful"
  | "unhelpful"
  | "expired"
  | "starred";

interface UseResponseCacheParams {
  selectedBot?: { botCustomId?: string };
  fromDate?: number;
  toDate?: number;
  selectedFilter?: QuestionFilterType;
  searchString?: string;
  page?: number;
  pageSize?: number;
  selectedCategory?: string;
  hidePagination?: any;
  calledFromBuilder?: any;
  sort?: any;
}

export interface QuestionSentiment {
  positive: number;
  negative: number;
  neutral: number;
}

export interface QuestionPagination {
  currentPage: number;
  pageSize: number;
  totalQuestions: number;
  totalPages: number;
}

export interface QuestionData {
  query: string;
  answer: string;
  answered: boolean;
  model: string;
  preserved: boolean;
}

export interface QuestionsResponse {
  questions: QuestionData[];
  sentiment: QuestionSentiment;
  pagination: QuestionPagination;
}
